import React, { useState, useEffect } from 'react';
import {
  listenToPayouts, addPayout, addMerchantDailyPayout, updatePayout, deletePayout,
  listenToOrders, listenToShops,
} from '../services/firebase';
import {
  computeDailyMerchantOwed, countDailyDeliveredOrders,
} from '../utils/merchantEarnings';
import { todayKey } from '../utils/dates';
import toast from 'react-hot-toast';

const emptyForm = {
  riderId: '', merchantId: '', shopName: '', amount: '', period: '',
  status: 'pending', notes: '', orderCount: '',
};

const hasBankDetails = (shop) =>
  shop && (shop.accountName || shop.bankName || shop.accountNumber || shop.ifsc || shop.upiId);

export default function Payouts() {
  const [payouts, setPayouts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [shops, setShops] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [filterMerchant, setFilterMerchant] = useState('');
  const [filterRider, setFilterRider] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const u1 = listenToPayouts(setPayouts);
    const u2 = listenToOrders(setOrders);
    const u3 = listenToShops(setShops);
    return () => { u1(); u2(); u3(); };
  }, []);

  const shopMap = Object.fromEntries(shops.map((s) => [s.id, s]));

  const filtered = payouts.filter((p) => {
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    if (filterMerchant && p.merchantId !== filterMerchant) return false;
    if (filterRider && p.riderId !== filterRider) return false;
    return true;
  });

  const merchantShops = shops.filter((s) => s.merchantId && s.active !== false);
  const riderIds = [...new Set(payouts.map((p) => p.riderId).filter(Boolean))];

  const openNew = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setForm({
      riderId: p.riderId || '',
      merchantId: p.merchantId || '',
      shopName: p.shopName || '',
      amount: p.amount ?? '',
      period: p.period || '',
      status: p.status || 'pending',
      notes: p.notes || '',
      orderCount: p.orderCount ?? '',
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const selectedShop = form.merchantId ? shopMap[form.merchantId] : null;

  const handleSave = async () => {
    if (!form.amount || !form.period) { toast.error('Amount and period required'); return; }
    if (!form.riderId && !form.merchantId) { toast.error('Rider ID or Merchant ID required'); return; }
    const payload = {
      riderId: form.riderId.trim() || null,
      merchantId: form.merchantId.trim() || null,
      shopName: form.shopName.trim() || selectedShop?.name || null,
      amount: Number(form.amount),
      period: form.period.trim(),
      status: form.status,
      notes: form.notes.trim(),
      orderCount: form.orderCount !== '' ? Number(form.orderCount) : null,
    };
    if (editingId) {
      await updatePayout(editingId, payload);
      toast.success('Payout updated');
    } else {
      await addPayout(payload);
      toast.success('Payout created');
    }
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payout record?')) return;
    await deletePayout(id);
    toast.success('Deleted');
  };

  const handleGenerateDaily = async () => {
    const period = todayKey();
    if (!window.confirm(`Generate pending merchant payouts for ${period}?`)) return;
    setGenerating(true);
    let created = 0;
    let skipped = 0;
    try {
      for (const shop of merchantShops) {
        const amount = computeDailyMerchantOwed(orders, shop.id, period);
        const orderCount = countDailyDeliveredOrders(orders, shop.id, period);
        if (amount <= 0) continue;

        const result = await addMerchantDailyPayout({
          merchantId: shop.id,
          shopName: shop.name,
          amount,
          period,
          status: 'pending',
          orderCount,
          notes: `Daily merchant earnings — ${period} (0% commission pilot)`,
        });
        if (result.skipped) skipped += 1;
        else created += 1;
      }
      toast.success(`Created ${created} payout(s) for ${period}${skipped ? ` (${skipped} already existed)` : ''}`);
    } catch (e) {
      toast.error('Could not generate payouts');
    } finally {
      setGenerating(false);
    }
  };

  const markPaid = async (p) => {
    await updatePayout(p.id, { status: 'paid' });
    toast.success('Marked as paid');
  };

  return (
    <div className="page-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Payouts</div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>{filtered.length} of {payouts.length} records</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-ghost" onClick={handleGenerateDaily} disabled={generating}>
            {generating ? 'Generating…' : `Generate daily merchant payouts (${todayKey()})`}
          </button>
          <button type="button" className="btn btn-green" onClick={openNew}>+ New payout</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <select value={filterMerchant} onChange={(e) => setFilterMerchant(e.target.value)} style={{ minWidth: 160 }}>
          <option value="">All merchants</option>
          {merchantShops.map((s) => (
            <option key={s.id} value={s.id}>{s.emoji} {s.name}</option>
          ))}
        </select>
        <select value={filterRider} onChange={(e) => setFilterRider(e.target.value)} style={{ minWidth: 140 }}>
          <option value="">All riders</option>
          {riderIds.map((id) => (
            <option key={id} value={id}>{id.slice(0, 12)}…</option>
          ))}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="pending">pending</option>
          <option value="paid">paid</option>
        </select>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr><th>PERIOD</th><th>SHOP</th><th>RIDER</th><th>AMOUNT</th><th>ORDERS</th><th>STATUS</th><th>ACTIONS</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text2)', padding: 32 }}>No payouts yet</td></tr>
            ) : filtered.map((p) => {
              const shop = p.merchantId ? shopMap[p.merchantId] : null;
              return (
                <tr key={p.id}>
                  <td>{p.period}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{p.shopName || shop?.name || '—'}</div>
                    {p.merchantId && (
                      <div style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: 'var(--text2)' }}>{p.merchantId.slice(0, 10)}…</div>
                    )}
                  </td>
                  <td style={{ fontFamily: 'JetBrains Mono', fontSize: 11 }}>{p.riderId ? `${p.riderId.slice(0, 10)}…` : '—'}</td>
                  <td style={{ fontFamily: 'JetBrains Mono', color: 'var(--green)', fontWeight: 700 }}>₹{p.amount}</td>
                  <td>{p.orderCount ?? '—'}</td>
                  <td><span className={`badge ${p.status === 'paid' ? 'badge-green' : 'badge-orange'}`}>{p.status}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {p.status === 'pending' && (
                      <button type="button" className="btn btn-green btn-sm" onClick={() => markPaid(p)}>Mark paid</button>
                    )}
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}>Edit</button>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => handleDelete(p.id)}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setShowForm(false)}>
          <div className="card" style={{ maxWidth: 480, width: '100%', padding: 24, maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{editingId ? 'Edit payout' : 'New payout'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <select
                value={form.merchantId}
                onChange={(e) => {
                  const id = e.target.value;
                  const shop = shopMap[id];
                  setForm({ ...form, merchantId: id, shopName: shop?.name || '' });
                }}
              >
                <option value="">Select merchant shop (optional)</option>
                {merchantShops.map((s) => (
                  <option key={s.id} value={s.id}>{s.emoji} {s.name}</option>
                ))}
              </select>
              <input placeholder="Rider ID (optional)" value={form.riderId} onChange={(e) => setForm({ ...form, riderId: e.target.value })} />
              <input type="number" placeholder="Amount ₹" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              <input placeholder="Period YYYY-MM-DD" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
              <input type="number" placeholder="Order count (optional)" value={form.orderCount} onChange={(e) => setForm({ ...form, orderCount: e.target.value })} />
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="pending">pending</option>
                <option value="paid">paid</option>
              </select>
              <input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

              {selectedShop && hasBankDetails(selectedShop) && (
                <div style={{ padding: 12, background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8, color: 'var(--text2)' }}>MERCHANT BANK / UPI</div>
                  {selectedShop.accountName && <div>Account: {selectedShop.accountName}</div>}
                  {selectedShop.bankName && <div>Bank: {selectedShop.bankName}</div>}
                  {selectedShop.accountNumber && <div>A/C: {selectedShop.accountNumber}</div>}
                  {selectedShop.ifsc && <div>IFSC: {selectedShop.ifsc}</div>}
                  {selectedShop.upiId && <div>UPI: {selectedShop.upiId}</div>}
                </div>
              )}
              {selectedShop && !hasBankDetails(selectedShop) && (
                <div style={{ fontSize: 12, color: 'var(--orange)' }}>⚠ No bank/UPI details on file for this shop</div>
              )}

              <button type="button" className="btn btn-green" onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
